import { useEffect, useState } from "react";
import { absoluteURL } from "src/lib/utils/absolute-url";
import { stripHtml } from "src/lib/utils/strip-html";
import { Category, Description } from "src/types";
import { CategoryLink } from "../category-link";
import useEmblaCarousel from 'embla-carousel-react'
import {
  NextButton,
  PrevButton,
  usePrevNextButtons
} from './carousel-arrow-buttons'
import { DotButton, useDotButton } from './carousel-dot-button';
import { ReactComponent as Caret } from "../../assets/caret-right.svg";

export function CategoryCarousel({ categories }: { categories: Category[]; }) {
  const [activeSlide, setActiveSlide] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true
  })

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  const { scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi)

  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => setActiveSlide(emblaApi.selectedScrollSnap());

    emblaApi.on('select', onSelect)
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    }

  }, [emblaApi])

  useEffect(() => {
    const category = categories[activeSlide];
    if (!category) {
      return;
    }

    setActiveSlide(activeSlide)
  }, [activeSlide, categories]);

  return (
    <div className="mb-8 -mx-3 md:-mx-10">
      <div className="relative overflow-hidden">
        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div
            className="flex touch-pan-y cursor-grab touch-pinch-zoom m-0"
            onPointerDown={(event) => { event.currentTarget.style.cursor = "grabbing"; }}
            onPointerUp={(event) => { event.currentTarget.style.cursor = "grab"; }}
          >
            {categories?.map((category, i) => (
              <CategorySlide
                category={category}
                showOverlay={i === activeSlide}
                key={i}
              />
            ))}
          </div>
        </div>
        <PrevButton
          onClick={onPrevButtonClick}
          disabled={prevBtnDisabled}
          className="bg-white/40 flex absolute top-1/2 translate-y-[-50%] justify-center items-center p-1 w-10 h-10 left-0"
        >
          <Caret className="stroke-white rotate-180 translate-y-0 mr-0.5" />
        </PrevButton>
        <NextButton
          onClick={onNextButtonClick}
          disabled={nextBtnDisabled}
          className="bg-white/40 flex absolute top-1/2 translate-y-[-50%] justify-center items-center p-1 w-10 h-10 right-0"
        >
          <Caret className="stroke-white ml-0.5" />
        </NextButton>
        <div className="flex flex-wrap justify-center items-center absolute w-full bottom-2">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={`
                block touch-manipulation cursor-pointer opacity-1 p-0 mx-1 my-0 w-3
                h-3 bg-[rgba(255, 255, 255, 0)]
                rounded-full border border-solid border-white
                ${index === 0 ? "rounded-xl w-[1.25rem]" : ""}
                ${index === 1 ? "ml-5" : ""}
                ${index === activeSlide ? "bg-white" : ""}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CategorySlide({ category, showOverlay }: { category: Category, showOverlay: boolean }) {
  return (
    <div className="min-w-0 pl-0 translate-x-0 translate-y-0 grow-0 shrink-0 basis-[706px]">
      <div
        className="h-[400px] w-full px-8 pt-14 pb-20 !bg-cover !bg-center !bg-no-repeat"
        style={{
          background: `url(${absoluteURL(category.field_category_icon?.uri.url)})`,
        }}
      ></div>
      <div className={
        `absolute inset-0 pointer-events-none transition-opacity duration-300 bg-[rgba(0,0,0,.5)]
        ${showOverlay ? "opacity-0" : "opacity-50"}
        `}></div>
        <Overlay
          title={category.name}
          description={category.description}
          to={category.path.alias}
          showOverlay={showOverlay}
        />
    </div>
  );
}

interface OverlayProps {
  title: string;
  description?: Description;
  to?: string;
  showOverlay: boolean
}

function Overlay({
  title,
  description,
  to,
  showOverlay
}: OverlayProps) {
  return (
    <div className={`
      transition-opacity duration-300 absolute w-full left-1/2 transform -translate-x-1/2 md:left-10 md:translate-x-0 top-1/2 -translate-y-1/2 pointer-events-none
      ${showOverlay ? "opacity-1" : "opacity-0"}
    `}>
      <div className="flex flex-col w-full max-w-[390px] h-[270] p-5 m-auto md:-m-0 md:ml-8 lg:m-0 bg-black/50 text-white">
        <p className="shrink-0 font-bold text-[35px] py-1 mb-3 border-t-4 border-b whitespace-nowrap overflow-hidden text-ellipsis">
          {title}
        </p>
        {description &&
          <p className="line-clamp-4">{stripHtml(description?.processed)}</p>
        }
        <CategoryLink
          to={to}
          textColorClass="text-white"
          textHoverColorClass="text-black"
          borderColorClass="border-white"
          bgHoverColorClass="bg-white"
          strokeColorClass="stroke-white"
          strokeHoverColorClass="stroke-black"
          className="inline-flex self-start mt-auto pointer-events-auto"
        />
      </div>
    </div>
  );
}
